import { can, PERMISSION } from '@wa/shared';

/**
 * The settings sections, in the order the column lists them.
 *
 * One registry, three consumers: the column, the routes, and the phone's index
 * screen. A section that exists in two of those and not the third is a link that
 * 404s, which is the failure this file exists to make impossible.
 *
 * `needs` is courtesy, not control — the routes and the API check it again. It is
 * here so a person who administers routing but not people sees one section rather
 * than five, four of which refuse them.
 */
export interface SettingsSection {
  key: string;
  label: string;
  /** One line under the label, the way the reference does it. Not a paragraph. */
  blurb: string;
  /**
   * Absent means everybody, and exactly one section uses that.
   *
   * It was required until Notifications arrived, and that was right for every
   * section that existed: each of them edits the account, so each has a permission.
   * Notifications is the first that edits only the person reading it — which
   * permission would even fit? `settings.write` would mean an agent needing an
   * administrator to switch off a sound that was annoying them, and inventing a
   * `notifications.own` that is granted to everybody is a permission that answers
   * no question.
   */
  needs?: string;
  icon: React.ReactNode;
}

/**
 * Whether this person may open a section. Absent `needs` is everybody — which is
 * the reason this is a function rather than `can(user, s.needs)` at each of the four
 * call sites: `can` answers false for undefined, so a missing permission would have
 * hidden the section from everyone rather than showing it to them.
 */
export function maySeeSection(
  user: Parameters<typeof can>[0], section: SettingsSection,
): boolean {
  return section.needs === undefined || can(user, section.needs);
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const SECTIONS: SettingsSection[] = [
  {
    key: 'people',
    label: 'Team',
    blurb: 'Who is on this account and whether they are taking chats',
    needs: PERMISSION.teamManage,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <circle cx="7.6" cy="7.4" r="2.5" {...stroke} />
        <path d="M3.2 16.2a4.6 4.6 0 0 1 8.8 0" {...stroke} />
        <path d="M13.2 5.4a2.4 2.4 0 0 1 0 4.5M14.4 16.2a4.4 4.4 0 0 0-1.1-2.9" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'roles',
    label: 'Roles',
    blurb: 'Edit and create the roles people are given',
    needs: PERMISSION.teamManage,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <circle cx="8.5" cy="6.6" r="2.6" {...stroke} />
        <path d="M3.6 16a4.9 4.9 0 0 1 9.8 0" {...stroke} />
        <path d="M13.6 9.2l1.2 1.2 2.4-2.6" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'hours',
    label: 'Working hours',
    blurb: 'When you are open, and what customers are told when you are not',
    needs: PERMISSION.teamManage,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <circle cx="10" cy="10" r="6.6" {...stroke} />
        <path d="M10 6.2V10l2.6 1.6" {...stroke} />
      </svg>
    ),
  },
  {
    // Between the account's settings and this person's: it is the only section here
    // that is theirs alone, and it sits high because it is the one somebody goes
    // looking for on their first morning.
    key: 'notifications',
    label: 'Notifications',
    blurb: 'What you are told about, and whether it pops up or makes a sound',
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <path d="M10 3.4a4.3 4.3 0 0 0-4.3 4.3v2.6L4.3 13h11.4l-1.4-2.7V7.7A4.3 4.3 0 0 0 10 3.4Z" {...stroke} />
        <path d="M8.2 15.1a1.9 1.9 0 0 0 3.6 0" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'routing',
    label: 'Chat assignment',
    blurb: 'Which bot, team and person a new chat goes to',
    needs: PERMISSION.automationWrite,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <circle cx="5" cy="10" r="1.9" {...stroke} />
        <circle cx="15" cy="5.6" r="1.9" {...stroke} />
        <circle cx="15" cy="14.4" r="1.9" {...stroke} />
        <path d="M6.7 9.1 13.3 6.3M6.7 10.9l6.6 2.8" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'numbers',
    label: 'Numbers',
    blurb: 'Your WhatsApp numbers, and what your team calls each one',
    needs: PERMISSION.settingsWrite,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <path d="M6.2 3.6h7.6a1.2 1.2 0 0 1 1.2 1.2v10.4a1.2 1.2 0 0 1-1.2 1.2H6.2A1.2 1.2 0 0 1 5 15.2V4.8a1.2 1.2 0 0 1 1.2-1.2Z" {...stroke} />
        <path d="M8.6 13.8h2.8" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'targets',
    label: 'Response targets',
    blurb: 'How fast a reply and a resolution should come, and what counts as late',
    needs: PERMISSION.settingsWrite,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <circle cx="10" cy="10" r="6.6" {...stroke} />
        <circle cx="10" cy="10" r="3" {...stroke} />
        <path d="M10 3.4V1.8M16.6 10h1.6M10 16.6v1.6M3.4 10H1.8" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'quick-replies',
    label: 'Quick replies',
    blurb: 'Sentences the team types often, one slash away in the inbox',
    needs: PERMISSION.cannedWrite,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <path d="M4 5.5h12M4 9.5h8M4 13.5h5" {...stroke} />
        <path d="m13 12 3 2-3 2" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'catalogue',
    label: 'Catalogue',
    blurb: 'The products customers can browse and order on WhatsApp',
    needs: PERMISSION.catalogueManage,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <path d="M4.6 7.2h10.8l-.9 8.6a1.2 1.2 0 0 1-1.2 1.1H6.7a1.2 1.2 0 0 1-1.2-1.1Z" {...stroke} />
        <path d="M7.4 7.2V6a2.6 2.6 0 0 1 5.2 0v1.2" {...stroke} />
      </svg>
    ),
  },
  {
    key: 'developer',
    label: 'API & webhooks',
    blurb: 'Keys for your own systems, and what we send them',
    needs: PERMISSION.settingsWrite,
    icon: (
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
        <path d="M7.2 6.4 3.6 10l3.6 3.6M12.8 6.4l3.6 3.6-3.6 3.6" {...stroke} />
        <path d="M11.3 4.6 8.7 15.4" {...stroke} />
      </svg>
    ),
  },
];
